/**
 * useConfigurationErrorToast — surface 412 IntegrationConfigRequired errors
 * with a deep-link "Configure now" action.
 *
 * Mounted once from Shell.jsx. Listens for the `configurationRequired` custom
 * event that api/client.js dispatches when a service returns HTTP 412 with
 * error=configuration_required. Shows a fixed-position banner with:
 *   - The provider + field that's missing
 *   - A "Configure now" button that navigates to `/admin?tab=...&card=...&focus=...`
 *
 * Banner auto-dismisses after 15 seconds or on route change. No new
 * dependency — uses existing COLORS tokens and inline styles to match the
 * established inline-banner UX (ComplianceTab, MCP registration success).
 */
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { COLORS } from "../theme/colors";

export function useConfigurationErrorToast() {
  const [banner, setBanner] = useState(null); // {provider, field, tab, card, message, key}
  const navigate = useNavigate();
  const location = useLocation();

  // Listen for 412 events from api/client.js
  useEffect(() => {
    const handler = (evt) => {
      const detail = evt?.detail || {};
      if (!detail.field) return;
      setBanner({
        provider: detail.provider || "",
        field: detail.field,
        tab: detail.tab || "apikeys",
        card: detail.card || null,
        message: detail.message || "",
        key: Date.now(),  // force re-render if a second 412 arrives
      });
    };
    window.addEventListener("configurationRequired", handler);
    return () => window.removeEventListener("configurationRequired", handler);
  }, []);

  // Auto-dismiss on route change — once admin lands on the Admin tab, the
  // banner is redundant.
  useEffect(() => { setBanner(null); }, [location.pathname, location.search]);

  // Auto-dismiss after 15s
  useEffect(() => {
    if (!banner) return;
    const t = setTimeout(() => setBanner(null), 15000);
    return () => clearTimeout(t);
  }, [banner?.key]);

  if (!banner) return null;

  const buildLink = () => {
    const params = new URLSearchParams();
    params.set("tab", banner.tab);
    if (banner.card) params.set("card", banner.card);
    params.set("focus", banner.field);
    return `/admin?${params.toString()}`;
  };

  const providerLabel = banner.provider
    ? banner.provider.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "Integration";

  return (
    <div
      role="alert"
      style={{
        position: "fixed",
        top: 16,
        right: 16,
        zIndex: 10000,
        maxWidth: 420,
        padding: "14px 16px",
        borderRadius: 10,
        background: COLORS.surface,
        border: `1px solid ${COLORS.orange || "#F59E0B"}40`,
        boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
        fontSize: 13,
        color: COLORS.text,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div
          style={{
            flex: "0 0 auto",
            width: 8, height: 8, marginTop: 6,
            borderRadius: "50%",
            background: COLORS.orange || "#F59E0B",
            boxShadow: `0 0 8px ${COLORS.orangeGlow || "rgba(245,158,11,0.4)"}`,
          }}
        />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {providerLabel} not configured
          </div>
          <div style={{ color: COLORS.textMuted, marginBottom: 10, lineHeight: 1.4 }}>
            {banner.message || `Missing field: ${banner.field}`}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => { navigate(buildLink()); setBanner(null); }}
              style={{
                padding: "6px 12px",
                borderRadius: 6,
                background: COLORS.accent,
                color: "#fff",
                border: "none",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Configure now
            </button>
            <button
              onClick={() => setBanner(null)}
              style={{
                padding: "6px 12px",
                borderRadius: 6,
                background: "transparent",
                color: COLORS.textMuted,
                border: `1px solid ${COLORS.border}`,
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
